import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  Card,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const ProjectGallery = ({ images = [], title, icon, link, description }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (images.length === 0) return null;

  const selectedImage = images[selectedIndex];

  const handleThumbnailClick = (index) => setSelectedIndex(index);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handlePrev = () => {
    setSelectedIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
  };

  const handleNext = () => {
    setSelectedIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  return (
    <Card sx={{ mb: 4, mt: 1 }}>
      <Box sx={{ width: 380, borderRadius: 2, overflow: "hidden" }}>
        {/* Main image */}
        <Box
          component="img"
          src={selectedImage}
          alt={title}
          onClick={handleOpenModal}
          sx={{
            width: "100%",
            height: 150,
            objectFit: "cover",
            cursor: "pointer",
            transition: "opacity 0.1s",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        />

        {/* Thumbnails */}
        <Grid container spacing={0.5} sx={{ p: 1 }}>
          {images.map((src, index) => (
            <Grid item xs={4} key={index}>
              <Box
                component="img"
                src={src}
                alt={`Thumbnail ${index + 1}`}
                onClick={() => handleThumbnailClick(index)}
                sx={{
                  width: "100%",
                  height: 30,
                  objectFit: "cover",
                  borderRadius: 1,
                  cursor: "pointer",
                  transition: "opacity 0.1s",
                  border:
                    selectedIndex === index
                      ? `2px solid #1976d2`
                      : "2px solid transparent",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>

        {/* Title, Icon, and Button */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 1, pt: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar
              src={icon}
              alt={`${title} icon`}
              sx={{ width: 24, height: 24 }}
            />
            <Typography variant="subtitle1" fontWeight="500">
              {title}
            </Typography>
          </Stack>

          {link && (
            <Button
              variant="text"
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              href={link}
              rel="noopener noreferrer"
              sx={{ textTransform: "none" }}
            >
              Link
            </Button>
          )}
        </Stack>

        {/* Modal with navigation */}
        <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="md">
          <DialogContent
            sx={{
              position: "relative",
              p: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
            >
              <CloseIcon />
            </IconButton>

            {/* Left Arrow */}
            <IconButton
              onClick={handlePrev}
              sx={{
                position: "absolute",
                left: 8,
                zIndex: 2,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            {/* Image */}
            <Box
              component="img"
              src={selectedImage}
              alt="Zoomed"
              sx={{
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
                mx: "auto",
              }}
            />

            {/* Right Arrow */}
            <IconButton
              onClick={handleNext}
              sx={{
                position: "absolute",
                right: 8,
                zIndex: 2,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </DialogContent>
        </Dialog>
      </Box>
    </Card>
  );
};

export default ProjectGallery;
